import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { CreditProgress } from '../components/ui/PageState';
import GoogleGIcon from '../components/GoogleGIcon';
import { needsPlanCheckout } from '../lib/billing';
import {
  clearPendingGoogleSignup,
  clearSignupToken,
  getPendingGoogleSignup,
  getSignupToken,
  mapGoogleAuthError,
  MappedGoogleAuthError,
  signInWithGoogle,
} from '../lib/googleAuth';
import { HOME_ROUTE, homePathForRole, type OrgRole } from '../lib/homeRoute';

let googleCallbackRun = false;

const FALLBACK_ERROR: MappedGoogleAuthError = {
  kind: 'generic',
  title: 'Não foi possível entrar',
  message: 'Não foi possível concluir o login com Google.',
};

export default function AuthCallback() {
  const { user, loading, needsSetup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<MappedGoogleAuthError | null>(null);
  const [status, setStatus] = useState('Entrando...');
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    const oauthError = searchParams.get('error_description') || searchParams.get('error');
    if (oauthError) {
      setError(mapGoogleAuthError(oauthError));
      clearPendingGoogleSignup();
      googleCallbackRun = false;
    }
  }, [searchParams]);

  useEffect(() => {
    if (loading || error) return;

    if (!user) {
      const timer = window.setTimeout(() => {
        setError(FALLBACK_ERROR);
        clearPendingGoogleSignup();
        googleCallbackRun = false;
      }, 8000);
      return () => window.clearTimeout(timer);
    }

    if (googleCallbackRun) return;
    googleCallbackRun = true;
    const signedInUser = user;

    async function finish(): Promise<void> {
      try {
        if (needsSetup) {
          clearPendingGoogleSignup();
          navigate('/setup-account', { replace: true });
          return;
        }

        const pending = getPendingGoogleSignup();
        const signupToken = searchParams.get('signup_token') || getSignupToken();
        // Plano fica para /assinar; o backend exige um nome — usamos Creator como base.
        const defaultPlanName = 'Creator';
        if (pending && signupToken) {
          setStatus('Preparando sua organização...');
          const { error: claimError } = await supabase.rpc('claim_signup_link', {
            p_token: signupToken,
            p_org_name: pending.org_name,
            p_plan_name: defaultPlanName,
          });
          if (claimError && !claimError.message.includes('already_subscribed')) {
            console.error('claim_signup_link', claimError);
            if (
              claimError.message.includes('signup_token')
              || claimError.message.includes('signup_cap')
            ) {
              clearSignupToken();
              clearPendingGoogleSignup();
              await supabase.rpc('discard_unclaimed_signup');
              await supabase.auth.signOut();
              googleCallbackRun = false;
              setError(mapGoogleAuthError(claimError.message));
              return;
            }
          }
        }

        if (pending) {
          setStatus('Preparando sua organização...');
          const { error: rpcError } = await supabase.rpc('apply_pending_self_serve_signup', {
            p_org_name: pending.org_name,
            p_plan_name: defaultPlanName,
          });

          clearPendingGoogleSignup();
          clearSignupToken();

          if (rpcError?.message.includes('already_subscribed')) {
            navigate(HOME_ROUTE, { replace: true });
            return;
          }

          if (rpcError?.message.includes('not_org_admin')) {
            await supabase.rpc('discard_unclaimed_signup');
            await supabase.auth.signOut();
            googleCallbackRun = false;
            setError(mapGoogleAuthError('signup_token_required'));
            return;
          }

          if (rpcError) {
            console.error('apply_pending_self_serve_signup', rpcError);
          }

          navigate('/assinar', { replace: true });
          return;
        }

        clearSignupToken();

        const { data: membership } = await supabase
          .from('organization_members')
          .select('role, organizations (stripe_subscription_id, stripe_subscription_status)')
          .eq('user_id', signedInUser.id)
          .maybeSingle();

        const rawOrg = membership?.organizations as
          | { stripe_subscription_id?: string | null; stripe_subscription_status?: string | null }
          | { stripe_subscription_id?: string | null; stripe_subscription_status?: string | null }[]
          | null;
        const org = Array.isArray(rawOrg) ? rawOrg[0] : rawOrg;
        const role = (membership?.role as OrgRole) || null;
        if (!membership) {
          clearSignupToken();
          await supabase.rpc('discard_unclaimed_signup');
          await supabase.auth.signOut();
          googleCallbackRun = false;
          setError(mapGoogleAuthError('signup_token_required'));
          return;
        }
        navigate(needsPlanCheckout(role, org) ? '/assinar' : homePathForRole(role), { replace: true });
      } catch (err) {
        console.error('Google auth callback', err);
        clearPendingGoogleSignup();
        navigate('/assinar', { replace: true });
      }
    }

    void finish();
  }, [error, loading, navigate, needsSetup, searchParams, user]);

  const handleRetryGoogle = async () => {
    setRetrying(true);
    const { error: oauthError } = await signInWithGoogle();
    if (oauthError) {
      setError(mapGoogleAuthError(oauthError.message));
      setRetrying(false);
    }
  };

  if (error) {
    const showRetry = error.kind === 'canceled' || error.kind === 'generic';

    return (
      <div className="glass-card auth-callback-card">
        <div className="auth-brand">
          <img src="/assets/icon-mark.png" alt="Seu.video" className="auth-brand-mark" />
        </div>
        <h1>{error.title}</h1>
        <p className="subtitle">{error.message}</p>

        <div className={`auth-status-banner ${error.kind === 'canceled' ? 'is-muted' : 'is-error'}`} role="alert">
          <AlertCircle size={18} aria-hidden="true" />
          <span>
            {error.kind === 'canceled'
              ? 'Nenhuma conta foi criada ou alterada.'
              : 'Você pode continuar com e-mail e senha.'}
          </span>
        </div>

        <div className="auth-callback-actions">
          {showRetry && (
            <button
              type="button"
              className="btn-google"
              onClick={handleRetryGoogle}
              disabled={retrying}
            >
              <GoogleGIcon />
              {retrying ? 'Redirecionando...' : 'Tentar com Google de novo'}
            </button>
          )}
          <Link to="/login" className="btn-primary auth-callback-primary-link">
            Voltar ao login
          </Link>
          <Link to="/register" className="auth-inline-link">
            Criar organização com e-mail
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card auth-callback-card" style={{ textAlign: 'center' }}>
      <div className="auth-brand">
        <img src="/assets/icon-mark.png" alt="Seu.video" className="auth-brand-mark" />
      </div>
      <h1 className="sr-only">Entrando no Seu.video</h1>
      <CreditProgress label={status} detail="Isso leva só alguns segundos. Não feche esta janela." />
    </div>
  );
}
